// src/pages/materials/MaterialsPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { App, FloatButton } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useUI } from "@/context/UIContext";

import VirtualGrid from "./components/VirtualGrid";
import useMaterials from "./hooks/useMaterials";
import Index from "./components/MaterialDrawer";

export default function MaterialsPage() {
  const { message } = App.useApp();
  const { setHeaderTitle, searchQuery } = useUI();

  const {
    list, loading, reload, create, update, remove,
    parents, childrenByParent, units, suppliers,
    getById,                      // ⬅️ новое
  } = useMaterials();

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // локальное состояние для свежей детали
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // каждый раз при открытии редактирования — тянем актуальную деталь
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!open || !editingId) {
        setDetail(null);
        setDetailLoading(false);
        return;
      }
      try {
        setDetailLoading(true);
        const fresh = await getById(editingId);
        if (!cancelled) setDetail(fresh);
      } catch (e) {
        if (!cancelled) {
          message.error("Не вдалося завантажити матеріал");
          setOpen(false);
          setEditingId(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, editingId, getById, message]);

  useEffect(() => {
    setHeaderTitle("Матеріали");
    return () => setHeaderTitle("WHFOF");
  }, [setHeaderTitle]);

  const filtered = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((m) => {
      const bag = [
        m?.name, m?.code, m?.color, m?.composition,
        m?.vendor, m?.supplier_name, m?.characteristics, m?.note,
      ].filter(Boolean).join(" ").toLowerCase();
      return bag.includes(q);
    });
  }, [list, searchQuery]);

  const itemsToShow = filtered.length ? filtered : list;

  const onAdd = () => {
    setEditingId(null);       // новый — без запроса к деталям
    setOpen(true);
  };

  const onItemClick = (m) => {
    setEditingId(m?._id || null);
    setOpen(true);            // триггерит useEffect выше → свежая деталь
  };

  const onClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const onSubmit = useCallback(
    async (values) => {
      try {
        if (editingId) {
          await update(editingId, values);
          message.success("Зміни збережено");
        } else {
          await create(values);
          message.success("Матеріал додано");
        }
        setOpen(false);
        setEditingId(null);
        await reload();
      } catch {
        message.error("Не вдалося зберегти матеріал");
      }
    },
    [editingId, update, create, reload, message]
  );

  const onDelete = useCallback(
    async (id) => {
      try {
        await remove(id);
        message.success("Матеріал видалено");
        setOpen(false);
        setEditingId(null);
        await reload();
      } catch {
        message.error("Не вдалося видалити матеріал");
      }
    },
    [remove, reload, message]
  );

  return (
    <div className="min-h-screen flex flex-col bg-base-200">
      <div className="px-4 py-2 text-sm opacity-70">
        list: {list.length} • filtered: {filtered.length} • show: {itemsToShow.length}
      </div>

      <div className="flex-1">
        <VirtualGrid items={itemsToShow} loading={loading} onItemClick={onItemClick} />
      </div>

      <Index
        open={open}
        onClose={onClose}
        onSubmit={onSubmit}
        onDelete={onDelete}
        editing={editingId ? detail : null}           // ⬅️ только свежие данные
        loadingDetail={!!editingId && detailLoading}  // ⬅️ индикатор загрузки
        parents={parents}
        childrenByParent={childrenByParent}
        units={units}
        suppliers={suppliers}
      />

      <FloatButton
        icon={<PlusOutlined />}
        type="primary"
        shape="circle"
        tooltip="Додати"
        onClick={onAdd}
        style={{
          right: 16,
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          boxShadow: "0 10px 24px rgba(0,0,0,.18)",
        }}
      />
    </div>
  );
}
